import React from "react";
import {
  AlertTriangle, ArrowRight, Check, CheckCircle2, ChevronRight, Copy,
  DollarSign, Eye, Flame, KeyRound, Package, Plus, RefreshCw, ScanLine,
  ShoppingCart, Sparkles, User, XCircle, Zap
} from "lucide-react";
import { FormField } from "../components/shared/FormField";
import { AccessMethodIcon } from "../components/shared/AccessMethodIcon";
import { Modal } from "../components/shared/Modal";
import { GameBannerCarousel } from "../components/game/GameBannerCarousel";
import { GameFaq } from "../components/game/GameFaq";
import { CredentialHistoryChips } from "../components/client/CredentialHistoryChips";
import { thumb200 } from "../lib/imageVariants";
import { useAppContext } from "../AppContext";

/* -----------------------------------------------------------------------------
 * StoreDetailPage — client-side game detail view (package grid + credential
 * form + verify button + add-to-cart). Extracted from the previously
 * monolithic App.js. Rendered when the client picks a game from the catalog.
 * --------------------------------------------------------------------------- */
export function StoreDetailPage() {
  const ctx = useAppContext();
  const {
    // Selection state
    selectedGame, setSelectedGame,
    pendingPkgs, setPendingPkgs,
    checkoutOpen, setCheckoutOpen,
    // Credential form
    playerId, setPlayerId, playerPassword, setPlayerPassword,
    playerServer, setPlayerServer, accessMethod, setAccessMethod,
    verifiedPlayerName, setVerifiedPlayerName,
    verifiedPlayerRegion, setVerifiedPlayerRegion,
    ffPendingConfirmation, setFfPendingConfirmation,
    isVerifyingId, idVerificationStatus, setIdVerificationStatus,
    handleVerifyPlayerId,
    // Cart
    cart, handleAddToCart,
    // Verification acknowledgement (restricted purchases)
    pendingVerifyPkg, setPendingVerifyPkg,
    verifyAcknowledged, setVerifyAcknowledged,
    // Pricing helpers
    isReseller, effectiveUsd,
    formatBs, formatUsd, formatMoney, usdToBs, rateReady, currency,
    // Banner
    activeGameBanner,
    // User
    user,
  } = ctx;

  // Read cred-history refresh key + selection handler from context (App.js
  // increments the key on successful cart-add so chips re-render live).
  const credHistoryRefreshKey = ctx.credHistoryRefreshKey || 0;
  const applyCredentialEntry = (entry) => {
    if (!entry) return;
    setPlayerId(entry.playerId || "");
    if (typeof setPlayerPassword === "function") setPlayerPassword(entry.playerPassword || "");
    if (typeof setPlayerServer === "function") setPlayerServer(entry.playerServer || "");
    if (typeof setAccessMethod === "function" && entry.accessMethod) setAccessMethod(entry.accessMethod);
    // Reset any verification state so the "Verificar" button runs again for
    // the freshly-picked account.
    if (typeof setIdVerificationStatus === "function") setIdVerificationStatus("idle");
    if (typeof setFfPendingConfirmation === "function") setFfPendingConfirmation(null);
    if (typeof setVerifiedPlayerName === "function") setVerifiedPlayerName(null);
    if (typeof setVerifiedPlayerRegion === "function") setVerifiedPlayerRegion(null);
  };

  return (
              <div className="space-y-3 sm:space-y-6" data-testid="package-selection-section">
                <div className="relative overflow-hidden rounded-2xl border border-ag7-purple/30 shadow-[0_20px_50px_-20px_rgba(147,51,234,0.4)]">
                  {selectedGame.image && (
                    <div className="absolute inset-0" aria-hidden="true">
                      <img src={selectedGame.image} alt="" className="w-full h-full object-cover scale-110 blur-md opacity-40" loading="lazy" decoding="async" />
                      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0812]/95 via-[#0a0812]/70 to-ag7-purple/25" />
                    </div>
                  )}
                  <div className="relative flex items-center justify-between gap-3 px-3 py-3 sm:px-5 sm:py-5">
                  <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                    <button onClick={() => setSelectedGame(null)} data-testid="back-to-store-btn"
                      className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl bg-ag7-surface-2/80 backdrop-blur-sm hover:bg-ag7-surface border border-[var(--ag7-border-soft)] hover:border-ag7-purple/50 text-white text-[10px] sm:text-xs font-bold flex-shrink-0 transition-colors">← VOLVER</button>
                    {selectedGame.image && (
                      <div className="hidden sm:block w-12 h-12 rounded-xl overflow-hidden border border-ag7-purple/50 shadow-[0_0_16px_rgba(147,51,234,0.35)] flex-shrink-0" aria-hidden="true">
                        <img src={selectedGame.image} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="text-lg sm:text-2xl font-heading font-black tracking-tight text-white truncate drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]">{selectedGame.name}</h3>
                      <p className="hidden sm:block text-xs text-zinc-300 font-mono">Recarga de Diamantes y Paquetes Oficiales</p>
                    </div>
                  </div>
                  </div>
                </div>

                {/* Active promotional banner for this game (renders nothing when absent) */}
                <GameBannerCarousel banner={activeGameBanner} />

                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                  {selectedGame.packages?.map((pkg, idx) => {
                    const displayUsd = effectiveUsd(pkg);
                    const money = formatMoney(displayUsd);
                    const hasResellerPrice = isReseller && pkg.resellerPriceUsd && Number(pkg.resellerPriceUsd) > 0;
                    const isPopular = !!pkg.isBestSeller;
                    const isPending = pendingPkgs?.some(p => p.id === pkg.id);
                    const isRestricted = !!pkg.requiresVerification;
                    const togglePending = () => {
                      // Removing selection is always instant
                      if (isPending) {
                        setPendingPkgs(prev => prev?.filter(p => p.id !== pkg.id));
                        return;
                      }
                      // Adding a restricted package requires explicit acknowledgement first
                      if (isRestricted) {
                        setVerifyAcknowledged(false);
                        setPendingVerifyPkg(pkg);
                        return;
                      }
                      setPendingPkgs(prev => [...prev, pkg]);
                    };
                    return (
                      <div key={pkg.id} data-testid={`package-card-${pkg.id}`}
                        onClick={togglePending}
                        className={`rounded-xl sm:rounded-2xl p-3 sm:p-5 border transition-[border-color,box-shadow,transform,background-color] duration-200 flex flex-col justify-between cursor-pointer relative ${
                          isPending ? "bg-ag7-purple/10 border-ag7-purple ring-2 ring-ag7-purple/30 shadow-[var(--ag7-glow-purple)]" : "bg-ag7-surface border-[var(--ag7-border-soft)] hover:border-ag7-purple/50 hover:shadow-[var(--ag7-glow-purple)] hover:-translate-y-0.5"
                        }`}>
                        {isPending && (
                          <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-purple-500 flex items-center justify-center shadow-lg shadow-purple-500/40" data-testid={`package-selected-check-${pkg.id}`}>
                            <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                          </div>
                        )}
                        {isRestricted && !isPending && (
                          <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full bg-amber-950/80 border border-amber-500/60 text-amber-300 text-[8px] sm:text-[9px] font-mono font-bold uppercase tracking-widest shadow" data-testid={`package-restricted-badge-${pkg.id}`}>
                            <AlertTriangle className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> <span className="hidden sm:inline">Verificar</span>
                          </div>
                        )}
                        <div>
                          {isPopular && <div data-testid={`package-bestseller-badge-${pkg.id}`} className="inline-flex items-center gap-1 px-2 sm:px-2.5 py-0.5 rounded-full bg-gradient-to-r from-orange-600 to-red-600 border border-orange-400/60 text-white text-[8px] sm:text-[10px] font-black uppercase tracking-widest mb-1.5 sm:mb-3 shadow-md shadow-orange-500/40"><Flame className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="currentColor" strokeWidth={0} /> MÁS POPULAR</div>}
                          <div className="text-center my-1.5 sm:my-3">
                            <div className="w-11 h-11 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-ag7-purple/15 to-ag7-cyan/10 border border-ag7-purple/30 flex items-center justify-center mx-auto mb-1 sm:mb-2 overflow-hidden">
                              {pkg.image ? (
                                <img
                                  src={thumb200(pkg.image)}
                                  alt={pkg.name}
                                  width={64}
                                  height={64}
                                  className="w-full h-full object-cover"
                                  data-testid={`package-img-${pkg.id}`}
                                  loading="lazy"
                                  decoding="async"
                                />
                              ) : (
                                <Sparkles className="w-5 h-5 sm:w-7 sm:h-7 text-purple-400" />
                              )}
                            </div>
                            <h4 className="font-heading font-extrabold text-white text-xs sm:text-lg leading-tight line-clamp-2">{pkg.name}</h4>
                            <p className="hidden sm:block text-[11px] text-zinc-400 mt-1">Entrega instantánea</p>
                            {pkg.description && (
                              <p
                                data-testid={`pkg-description-${pkg.id}`}
                                className="hidden sm:block text-[11px] text-cyan-300/80 mt-2 italic leading-snug"
                                title={pkg.description}
                              >
                                {pkg.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 pt-2 sm:mt-4 sm:pt-4 border-t border-[var(--ag7-border-soft)] text-center space-y-1.5 sm:space-y-3">
                          <div>
                            <span data-testid={`pkg-price-${pkg.id}`} className="text-base sm:text-xl font-extrabold text-ag7-cyan font-mono tabular-nums">{money.primary}</span>
                            {hasResellerPrice && (
                              <p className="text-[9px] sm:text-[10px] text-zinc-500 font-mono line-through">${pkg.priceUsd.toFixed(2)} USD</p>
                            )}
                            <p className={`text-[9px] sm:text-[10px] font-mono ${hasResellerPrice ? "text-amber-300 font-bold" : "text-zinc-500"}`}>{money.secondary}{hasResellerPrice ? " · MAYORISTA" : ""}</p>
                          </div>
                          <button onClick={(e) => { e.stopPropagation(); togglePending(); }}
                            data-testid={`package-select-btn-${pkg.id}`}
                            className={`w-full font-bold py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-[10px] sm:text-xs shadow-md transition-[opacity,background-color,box-shadow] ${
                              isPending
                                ? "bg-ag7-surface-2 hover:bg-zinc-700 border border-[var(--ag7-border-soft)] text-white"
                                : "bg-gradient-to-r from-ag7-purple to-ag7-magenta hover:opacity-90 text-white shadow-ag7-purple/30"
                            }`}>
                            {isPending ? "QUITAR" : "COMPRAR"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* FAQ section per game */}
                <GameFaq game={selectedGame} />

                {/* Floating "Realizar Pago" action bar — appears when user has selected 1+ packages */}
                {pendingPkgs.length > 0 && !checkoutOpen && (
                  <div
                    data-testid="pending-checkout-bar"
                    className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-2xl"
                  >
                    <div className="rounded-2xl border border-ag7-purple/50 bg-[var(--ag7-glass)] backdrop-blur-xl shadow-[0_10px_40px_-5px_rgba(147,51,234,0.55)] p-3 sm:p-4 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            data-testid="pending-checkout-count"
                            className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-ag7-purple text-white text-[11px] font-black font-mono"
                          >
                            {pendingPkgs.length}
                          </span>
                          <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 truncate">
                            {pendingPkgs.length === 1 ? "paquete seleccionado" : "paquetes seleccionados"}
                          </span>
                        </div>
                        <p className="text-ag7-cyan font-mono font-black tabular-nums text-base sm:text-lg mt-0.5" data-testid="pending-checkout-total">
                          {(() => {
                            const totalUsd = pendingPkgs?.reduce((a, p) => a + Number(effectiveUsd(p) || 0), 0);
                            return currency === "USD" ? formatUsd(totalUsd) : formatBs(usdToBs(totalUsd));
                          })()}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPendingPkgs([])}
                        data-testid="pending-checkout-clear-btn"
                        className="px-3 py-2 rounded-xl bg-ag7-surface-2 hover:bg-zinc-700 border border-[var(--ag7-border-soft)] text-zinc-200 text-[11px] font-bold transition-colors"
                        title="Vaciar selección"
                      >
                        Vaciar
                      </button>
                      <button
                        type="button"
                        onClick={() => setCheckoutOpen(true)}
                        disabled={!rateReady}
                        data-testid="realizar-pago-btn"
                        title={rateReady ? undefined : "Esperando tasa oficial USD/Bs..."}
                        className="px-5 py-3 rounded-xl bg-gradient-to-r from-ag7-purple to-ag7-magenta hover:opacity-90 text-white font-black text-xs sm:text-sm shadow-lg shadow-ag7-purple/30 disabled:opacity-50 disabled:cursor-wait transition-opacity"
                      >
                        {rateReady ? "REALIZAR PAGO →" : "Cargando tasa..."}
                      </button>
                    </div>
                  </div>
                )}

                {checkoutOpen && pendingPkgs.length > 0 && (
                  <Modal
                    onClose={() => { setCheckoutOpen(false); setPlayerId(""); setPlayerPassword(""); setPlayerServer(""); setAccessMethod(""); setIdVerificationStatus("idle"); setVerifiedPlayerName(null); }}
                    size="md"
                    testId="package-details-modal"
                  >
                    <div className="space-y-5">
                      <div className="flex items-start gap-3 border-b border-[var(--ag7-border)] pb-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ag7-purple/20 to-ag7-cyan/10 border border-ag7-purple/40 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {selectedGame.image ? (
                            <img src={thumb200(selectedGame.image)} alt="" width={80} height={80} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                          ) : (
                            <Sparkles className="w-6 h-6 text-purple-400" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-mono uppercase tracking-widest text-ag7-purple">Paso 2 · Datos de cuenta</p>
                          <h4 className="font-heading font-extrabold text-white text-base sm:text-lg truncate">
                            {selectedGame.name}
                          </h4>
                          <p className="text-xs text-zinc-400 truncate">
                            {pendingPkgs.length} {pendingPkgs.length === 1 ? "paquete" : "paquetes"} · <strong className="text-ag7-cyan font-mono tabular-nums">{(() => {
                              const totalUsd = pendingPkgs?.reduce((a, p) => a + Number(effectiveUsd(p) || 0), 0);
                              return currency === "USD" ? formatUsd(totalUsd) : formatBs(usdToBs(totalUsd));
                            })()}</strong>
                          </p>
                        </div>
                      </div>

                      {/* Selected packages summary */}
                      <div className="rounded-xl border border-[var(--ag7-border-soft)] bg-ag7-bg/60 divide-y divide-zinc-800/70 max-h-40 overflow-y-auto" data-testid="checkout-pending-list">
                        {pendingPkgs?.map((pkg) => (
                          <div key={pkg.id} className="flex items-center gap-3 px-3 py-2" data-testid={`checkout-pending-item-${pkg.id}`}>
                            <div className="w-8 h-8 rounded-lg bg-ag7-surface-2 border border-[var(--ag7-border-soft)] flex items-center justify-center overflow-hidden flex-shrink-0">
                              {pkg.image ? <img src={thumb200(pkg.image)} alt="" width={32} height={32} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <Sparkles className="w-4 h-4 text-purple-400" />}
                            </div>
                            <p className="text-xs text-zinc-200 truncate flex-1">{pkg.name}</p>
                            <span className="text-[11px] font-mono font-bold text-ag7-cyan tabular-nums whitespace-nowrap">{formatMoney(effectiveUsd(pkg)).primary}</span>
                            <button
                              type="button"
                              onClick={() => setPendingPkgs(prev => {
                                const next = prev?.filter(p => p.id !== pkg.id);
                                if (next.length === 0) { setCheckoutOpen(false); }
                                return next;
                              })}
                              data-testid={`checkout-remove-pkg-${pkg.id}`}
                              className="text-zinc-500 hover:text-red-400 p-1"
                              title="Quitar"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>

                      {(() => {
                        const ct = selectedGame.credentialType || "id";
                        if (ct === "username_password") {
                          return (
                            // ==== Games that need username + password (e.g. Roblox) ====
                            <div className="space-y-4">
                              <div className="p-3 rounded-xl bg-yellow-950/30 border border-yellow-500/40 text-yellow-200 text-[11px]">
                                🔐 Para procesar tu recarga necesitamos ingresar directamente a tu cuenta. Tus datos están cifrados y solo son visibles para el equipo de recarga.
                              </div>
                              <div>
                                <label className="block text-xs font-mono text-zinc-400 mb-1">{selectedGame.idLabel}</label>
                                <input
                                  type="text"
                                  data-testid="player-id-input"
                                  value={playerId}
                                  onChange={(e) => setPlayerId(e.target.value)}
                                  placeholder={selectedGame.idPlaceholder}
                                  autoFocus
                                  className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-ag7-purple"
                                />
                                <CredentialHistoryChips
                                  gameId={selectedGame.id}
                                  credType="username_password"
                                  refreshKey={credHistoryRefreshKey}
                                  onSelect={applyCredentialEntry}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-mono text-zinc-400 mb-1">Contraseña de {selectedGame.name}</label>
                                <input
                                  type="password"
                                  data-testid="player-password-input"
                                  value={playerPassword}
                                  onChange={(e) => setPlayerPassword(e.target.value)}
                                  placeholder="Contraseña de tu cuenta"
                                  className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-ag7-purple"
                                />
                              </div>
                              {selectedGame.hasAccessMethod && (selectedGame.accessMethods || []).length > 0 && (
                                <div>
                                  <label className="block text-xs font-mono text-zinc-400 mb-1 uppercase tracking-wider">
                                    Forma de Acceso
                                  </label>
                                  <p className="text-[10px] text-zinc-500 mb-2">
                                    ¿Con qué proveedor inicias sesión en {selectedGame.name}?
                                  </p>
                                  <div className="flex flex-wrap gap-2" data-testid="access-method-group">
                                    {selectedGame.accessMethods.map((opt) => (
                                      <button
                                        key={opt}
                                        type="button"
                                        onClick={() => setAccessMethod(opt)}
                                        data-testid={`access-method-${opt.split(" ")[0].toLowerCase()}`}
                                        className={`flex-1 min-w-[45%] sm:min-w-[30%] px-2 py-2.5 rounded-xl text-[11px] font-bold border inline-flex items-center justify-center gap-1.5 transition-[border-color,background-color,box-shadow] ${
                                          accessMethod === opt
                                            ? "bg-ag7-purple border-ag7-purple/60 text-white shadow-md shadow-ag7-purple/40"
                                            : "bg-ag7-bg border-[var(--ag7-border-soft)] text-zinc-300 hover:border-ag7-purple/60"
                                        }`}
                                      >
                                        <AccessMethodIcon name={opt} className="w-3.5 h-3.5 flex-shrink-0" />
                                        <span className="truncate">{opt}</span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        }
                        if (ct === "email") {
                          return (
                            // ==== Platforms that only need an email (e.g. Zinli, Binance) ====
                            <div className="space-y-4">
                              <div className="p-3 rounded-xl bg-blue-950/30 border border-blue-500/40 text-blue-200 text-[11px]">
                                ✉️ Ingresa el <strong>correo electrónico</strong> asociado a tu cuenta de {selectedGame.name}.
                              </div>
                              <div>
                                <label className="block text-xs font-mono text-zinc-400 mb-1">{selectedGame.idLabel || "Correo electrónico"}</label>
                                <input
                                  type="email"
                                  data-testid="player-id-input"
                                  value={playerId}
                                  onChange={(e) => setPlayerId(e.target.value)}
                                  placeholder={selectedGame.idPlaceholder || "tucorreo@dominio.com"}
                                  autoFocus
                                  inputMode="email"
                                  autoCapitalize="none"
                                  autoCorrect="off"
                                  className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-blue-500"
                                />
                                <CredentialHistoryChips
                                  gameId={selectedGame.id}
                                  credType="email"
                                  refreshKey={credHistoryRefreshKey}
                                  onSelect={applyCredentialEntry}
                                />
                              </div>
                            </div>
                          );
                        }
                        if (ct === "phone") {
                          return (
                            // ==== Platforms that only need a phone number (e.g. Wally, Pago Móvil) ====
                            <div className="space-y-4">
                              <div className="p-3 rounded-xl bg-emerald-950/30 border border-emerald-500/40 text-emerald-200 text-[11px]">
                                📱 Ingresa el <strong>número de teléfono</strong> asociado a tu cuenta de {selectedGame.name}.
                              </div>
                              <div>
                                <label className="block text-xs font-mono text-zinc-400 mb-1">{selectedGame.idLabel || "Número de teléfono"}</label>
                                <input
                                  type="tel"
                                  data-testid="player-id-input"
                                  value={playerId}
                                  onChange={(e) => setPlayerId(e.target.value)}
                                  placeholder={selectedGame.idPlaceholder || "Ej. 04121234567"}
                                  autoFocus
                                  inputMode="tel"
                                  className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-emerald-500"
                                />
                                <CredentialHistoryChips
                                  gameId={selectedGame.id}
                                  credType="phone"
                                  refreshKey={credHistoryRefreshKey}
                                  onSelect={applyCredentialEntry}
                                />
                              </div>
                            </div>
                          );
                        }
                        if (ct === "id_server") {
                          return (
                            // ==== Games that need ID + Server (e.g. Mobile Legends) ====
                            <div className="space-y-4">
                              <div className="p-3 rounded-xl bg-cyan-950/30 border border-cyan-500/40 text-cyan-200 text-[11px]">
                                🎮 Este juego requiere tu <strong>ID de jugador</strong> y el <strong>ID de servidor</strong>. En Mobile Legends los encuentras en tu perfil → tocar tu avatar → aparece como <strong>12345678 (1234)</strong>. El primer número es tu ID y el número entre paréntesis es el servidor.
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div className="sm:col-span-2">
                                  <label className="block text-xs font-mono text-zinc-400 mb-1">{selectedGame.idLabel}</label>
                                  <input
                                    type="text"
                                    data-testid="player-id-input"
                                    value={playerId}
                                    onChange={(e) => setPlayerId(e.target.value)}
                                    placeholder={selectedGame.idPlaceholder || "Ej. 12345678"}
                                    autoFocus
                                    inputMode="numeric"
                                    className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-ag7-purple"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-mono text-zinc-400 mb-1">ID Servidor</label>
                                  <input
                                    type="text"
                                    data-testid="player-server-input"
                                    value={playerServer}
                                    onChange={(e) => setPlayerServer(e.target.value)}
                                    placeholder="Ej. 1234"
                                    inputMode="numeric"
                                    className="w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-ag7-purple"
                                  />
                                </div>
                              </div>
                              <CredentialHistoryChips
                                gameId={selectedGame.id}
                                credType="id_server"
                                refreshKey={credHistoryRefreshKey}
                                onSelect={applyCredentialEntry}
                              />
                            </div>
                          );
                        }
                        // ==== Games that only need ID ====
                        return (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-mono text-zinc-400 mb-1">{selectedGame.idLabel}</label>
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                              <input
                                type="text"
                                data-testid="player-id-input"
                                value={playerId}
                                onChange={(e) => {
                                  setPlayerId(e.target.value);
                                  setIdVerificationStatus("idle");
                                  setFfPendingConfirmation(null);
                                  setVerifiedPlayerName(null);
                                  setVerifiedPlayerRegion(null);
                                }}
                                placeholder={selectedGame.idPlaceholder}
                                autoFocus
                                className="flex-1 bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-ag7-purple"
                              />
                              <button
                                type="button"
                                data-testid="verify-player-id-btn"
                                onClick={handleVerifyPlayerId}
                                disabled={isVerifyingId}
                                className="bg-ag7-cyan hover:opacity-90 text-white font-bold px-5 py-3 rounded-xl text-xs flex items-center justify-center gap-2 disabled:opacity-50 shadow-md shadow-ag7-cyan/25 transition-opacity"
                              >
                                {isVerifyingId ? (
                                  <>
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    <span className="whitespace-nowrap">
                                      {selectedGame?.slug === "ff" || selectedGame?.slug === "free-fire" || selectedGame?.slug === "freefire"
                                        ? "Consultando jugador..."
                                        : "Buscando..."}
                                    </span>
                                  </>
                                ) : (
                                  <span>
                                    {selectedGame?.slug === "ff" || selectedGame?.slug === "free-fire" || selectedGame?.slug === "freefire"
                                      ? "Consultar ID"
                                      : "Verificar"}
                                  </span>
                                )}
                              </button>
                            </div>
                            <CredentialHistoryChips
                              gameId={selectedGame.id}
                              credType="id"
                              refreshKey={credHistoryRefreshKey}
                              onSelect={applyCredentialEntry}
                            />
                          </div>

                          {/* Free Fire: confirmation box after successful lookup */}
                          {idVerificationStatus === "pending_confirmation" && ffPendingConfirmation && (
                            <div
                              data-testid="ff-confirmation-box"
                              className="p-4 rounded-xl bg-cyan-950/40 border-2 border-cyan-500/60 space-y-3"
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-cyan-500/20 border border-cyan-400/60 flex items-center justify-center flex-shrink-0">
                                  <CheckCircle2 className="w-5 h-5 text-cyan-300" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 mb-1">
                                    Confirma tu cuenta
                                  </p>
                                  <p className="text-white text-sm font-bold mb-0.5">
                                    ¿Eres tú?
                                  </p>
                                  <p
                                    data-testid="ff-confirmation-nickname"
                                    className="text-cyan-200 font-mono text-base break-all"
                                  >
                                    {ffPendingConfirmation.nickname}
                                  </p>
                                  <p
                                    data-testid="ff-confirmation-region"
                                    className="text-zinc-400 text-xs mt-1"
                                  >
                                    Región: <span className="text-white font-mono">{ffPendingConfirmation.region}</span>
                                  </p>
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-cyan-500/25">
                                <button
                                  type="button"
                                  data-testid="ff-confirm-reject-btn"
                                  onClick={() => {
                                    setFfPendingConfirmation(null);
                                    setIdVerificationStatus("idle");
                                    setPlayerId("");
                                  }}
                                  className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-bold px-4 py-2.5 rounded-lg text-xs"
                                >
                                  No, corregir ID
                                </button>
                                <button
                                  type="button"
                                  data-testid="ff-confirm-accept-btn"
                                  onClick={() => {
                                    setVerifiedPlayerName(ffPendingConfirmation.nickname);
                                    setVerifiedPlayerRegion(ffPendingConfirmation.region);
                                    setIdVerificationStatus("success");
                                    setFfPendingConfirmation(null);
                                  }}
                                  className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 text-white font-bold px-4 py-2.5 rounded-lg text-xs flex items-center justify-center gap-2"
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                  Sí, continuar al pago
                                </button>
                              </div>
                            </div>
                          )}

                          {idVerificationStatus === "success" && (
                            <div className="p-3 rounded-xl bg-green-950/40 border border-green-500/50 text-green-300 text-xs flex items-center gap-2" data-testid="id-verified-badge">
                              <CheckCircle2 className="w-4 h-4 text-green-400" />
                              <span>
                                Jugador confirmado: <strong className="text-white font-mono">{verifiedPlayerName}</strong>
                                {verifiedPlayerRegion && (
                                  <span className="text-zinc-400"> · Región: <span className="text-white font-mono">{verifiedPlayerRegion}</span></span>
                                )}
                              </span>
                            </div>
                          )}
                          {idVerificationStatus === "warning" && (
                            <div
                              data-testid="id-verification-warning"
                              className="p-3 rounded-xl bg-amber-950/40 border border-amber-500/50 text-amber-200 text-xs flex items-start gap-2"
                            >
                              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                              <span>
                                No pudimos verificar tu Nickname en este momento. Asegúrate de que el ID esté correcto antes de pagar.
                              </span>
                            </div>
                          )}
                          {idVerificationStatus === "error" && (
                            <div data-testid="id-verification-error" className="p-3 rounded-xl bg-red-950/40 border border-red-500/50 text-red-300 text-xs flex items-center gap-2">
                              <XCircle className="w-4 h-4 text-red-400" />
                              <span>
                                {selectedGame?.slug === "ff" || selectedGame?.slug === "free-fire" || selectedGame?.slug === "freefire"
                                  ? "ID no válido. Revisa el ID e intenta nuevamente."
                                  : "ID no encontrado o inválido"}
                              </span>
                            </div>
                          )}
                        </div>
                        );
                      })()}

                      <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-[var(--ag7-border-soft)]">
                        <button
                          type="button"
                          onClick={() => { setCheckoutOpen(false); setPlayerId(""); setPlayerPassword(""); setPlayerServer(""); setAccessMethod(""); setIdVerificationStatus("idle"); setVerifiedPlayerName(null); }}
                          data-testid="cancel-package-modal-btn"
                          className="flex-1 bg-ag7-surface-2 hover:bg-zinc-700 border border-[var(--ag7-border-soft)] text-white font-bold px-6 py-3 rounded-xl text-xs order-2 sm:order-1 transition-colors"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          data-testid="add-to-cart-btn"
                          disabled={(() => {
                            if (!rateReady) return true; // block until we know USD/Bs
                            if (!playerId.trim()) return true;
                            const ct = selectedGame.credentialType || "id";
                            if (ct === "username_password") {
                              if (!playerPassword) return true;
                              if (selectedGame.hasAccessMethod && !accessMethod) return true;
                              return false;
                            }
                            if (ct === "id_server") return !playerServer.trim();
                            if (ct === "email" || ct === "phone") return false;
                            // Enable the CTA both on a hard success AND on
                            // a soft-verify warning (FF Community API
                            // down/exhausted → admin verifies manually).
                            return idVerificationStatus !== "success" && idVerificationStatus !== "warning";
                          })()}
                          onClick={handleAddToCart}
                          className="flex-1 bg-gradient-to-r from-green-600 to-emerald-500 hover:opacity-90 text-white font-bold px-8 py-3 rounded-xl text-xs shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 order-1 sm:order-2"
                        >
                          <Plus className="w-4 h-4" />
                          <span>{pendingPkgs.length > 1 ? `Añadir ${pendingPkgs.length} al Carrito` : "Añadir al Carrito"}</span>
                        </button>
                      </div>
                    </div>
                  </Modal>
                )}

                {/* Restricted-purchase verification modal */}
                {pendingVerifyPkg && (
                  <Modal
                    onClose={() => { setPendingVerifyPkg(null); setVerifyAcknowledged(false); }}
                    size="md"
                    testId="pkg-verify-modal"
                  >
                    <div className="space-y-4">
                      <div className="flex items-start gap-3 border-b border-amber-500/25 pb-3">
                        <div className="w-11 h-11 rounded-xl bg-amber-500/15 border border-amber-500/50 flex items-center justify-center flex-shrink-0 shadow-inner shadow-amber-500/20">
                          <AlertTriangle className="w-6 h-6 text-amber-300" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-mono uppercase tracking-widest text-amber-300">Verificación requerida</p>
                          <h4 data-testid="pkg-verify-title" className="font-extrabold text-white text-base sm:text-lg leading-tight">
                            {(pendingVerifyPkg.verificationGuide?.title || pendingVerifyPkg.name || "Verifica antes de comprar")}
                          </h4>
                          <p className="text-xs text-zinc-400 truncate mt-0.5">
                            {selectedGame?.name} · <strong className="text-ag7-cyan font-mono tabular-nums">{formatBs(usdToBs(effectiveUsd(pendingVerifyPkg)))}</strong>
                          </p>
                        </div>
                      </div>

                      {pendingVerifyPkg.verificationGuide?.description && (
                        <p data-testid="pkg-verify-description" className="text-sm text-zinc-200 leading-relaxed">
                          {pendingVerifyPkg.verificationGuide.description}
                        </p>
                      )}

                      {pendingVerifyPkg.verificationGuide?.imageUrl && (
                        <div className="rounded-xl overflow-hidden border border-amber-500/25 bg-black">
                          <img
                            data-testid="pkg-verify-image"
                            src={pendingVerifyPkg.verificationGuide.imageUrl}
                            alt="Guía de verificación"
                            className="w-full max-h-56 object-contain"
                          />
                        </div>
                      )}

                      {Array.isArray(pendingVerifyPkg.verificationGuide?.steps) && pendingVerifyPkg.verificationGuide.steps.length > 0 && (
                        <ol className="space-y-2" data-testid="pkg-verify-steps">
                          {pendingVerifyPkg.verificationGuide.steps?.map((s, i) => (
                            <li key={`step-${i}-${s.slice(0, 40)}`} className="flex items-start gap-3">
                              <span className="mt-0.5 w-5 h-5 rounded-full bg-amber-500/20 border border-amber-500/50 text-amber-300 text-[10px] font-mono font-bold flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <span className="text-xs text-zinc-200 leading-relaxed">{s}</span>
                            </li>
                          ))}
                        </ol>
                      )}

                      <label className="flex items-start gap-3 p-3 rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)] cursor-pointer select-none hover:border-amber-500/40 transition-colors">
                        <input
                          type="checkbox"
                          data-testid="pkg-verify-checkbox"
                          checked={verifyAcknowledged}
                          onChange={(e) => setVerifyAcknowledged(e.target.checked)}
                          className="mt-0.5 w-4 h-4 accent-amber-500 flex-shrink-0"
                        />
                        <span className="text-xs text-zinc-200 leading-snug">
                          Confirmo que he verificado mi cuenta y tengo este paquete disponible.
                        </span>
                      </label>

                      <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-[var(--ag7-border-soft)]">
                        <button
                          type="button"
                          onClick={() => { setPendingVerifyPkg(null); setVerifyAcknowledged(false); }}
                          data-testid="pkg-verify-cancel-btn"
                          className="flex-1 bg-ag7-surface-2 hover:bg-zinc-700 border border-[var(--ag7-border-soft)] text-white font-bold px-6 py-3 rounded-xl text-xs order-2 sm:order-1 transition-colors"
                        >
                          Cancelar / Volver
                        </button>
                        <button
                          type="button"
                          disabled={!verifyAcknowledged}
                          data-testid="pkg-verify-continue-btn"
                          onClick={() => {
                            setPendingPkgs(prev => prev?.some(p => p.id === pendingVerifyPkg.id)
                              ? prev
                              : [...prev, pendingVerifyPkg]);
                            setPendingVerifyPkg(null);
                            setVerifyAcknowledged(false);
                          }}
                          className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-black font-black px-6 py-3 rounded-xl text-xs shadow-lg disabled:opacity-40 disabled:cursor-not-allowed order-1 sm:order-2"
                        >
                          Continuar al Pago →
                        </button>
                      </div>
                    </div>
                  </Modal>
                )}
              </div>
  );
}
